import {Inject, Injectable, Injector, runInInjectionContext, signal, inject} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router, UrlTree} from "@angular/router";
import {DOCUMENT} from "@angular/common";
import {getAllRouteData, getDeepestChildRoute} from "../utils";
import {assert} from "typia";
import {filter} from "rxjs";
import {BackNavigationService} from "./back-navigation.service";

const tabRoutes = ['/applications', '/support', '/profile', '/lotteries'];

@Injectable()
export class BrowserBackNavigationService extends BackNavigationService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly injector = inject(Injector);
  private readonly storageKey = 'navigationHistory';
  private history: string[] = [];
  private readonly method = signal<'forward' | 'backward'>('forward');

  private pathOf(url: string): string {
    const tree: UrlTree = this.router.parseUrl(url);
    const primary = tree.root.children['primary'];
    return primary ? '/' + primary.segments.map(s => s.path).join('/') : '/';
  }

  constructor(@Inject(DOCUMENT) private readonly doc: Document) {
    super();
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
      )
      .subscribe((ev) => {
        const isPop = ev.navigationTrigger === 'popstate' || this.method() === 'backward';
        const isBack = isPop;

        this.method.set("forward");
        if ((tabRoutes.includes(this.pathOf(ev.url)) && !isPop) || this.pathOf(ev.url) === this.pathOf(this.router.url)) {
          this.doc.documentElement.classList.remove('is-back', 'is-forward');
          return;
        }

        if(ev.navigationTrigger !== 'popstate') {
          this.doc.documentElement.classList.toggle('is-back', isBack);
          this.doc.documentElement.classList.toggle('is-forward', !isBack);
        }
      });

    const storedHistory = sessionStorage.getItem(this.storageKey);
    if (storedHistory) {
      try {
        this.history = assert<string[]>(JSON.parse(storedHistory));
      } catch (e) {
        console.error('Error parsing stored navigation history', e);
        this.history = [];
      }
    }

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (this.history.length > 0 && this.history[this.history.length - 1] === event.urlAfterRedirects) {
          return;
        }

        this.history.push(event.urlAfterRedirects);
        sessionStorage.setItem(this.storageKey, JSON.stringify(this.history));
      });
  }

  getBackRoute(): string {
    if (this.history.length > 1) {
      this.history.pop();
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.history));
      return this.history[this.history.length - 1];
    } else {
      return '/';
    }
  }

  public initialize() {
    /**App.addListener('backButton', (event) => {
        if (event.canGoBack) {
          this.navigateBack();
        } else {
          App.exitApp().catch(e => console.error(e));
        }
    });**/
  }

  public navigateBack(): void {
    this.method.set("backward");
    const backRoute = getAllRouteData(this.activatedRoute);

    const backRouteValue = backRoute.backRoute;
    const resolvedBackRoute = typeof backRouteValue === 'function'
      ? runInInjectionContext(this.injector, backRouteValue)
      : backRouteValue;

    if (resolvedBackRoute) {
      this.router.navigate(resolvedBackRoute, {
        replaceUrl: true,
        relativeTo: getDeepestChildRoute(this.activatedRoute),
        state: {backNavigation: true}
      });
    } else {
      this.router.navigateByUrl(this.getBackRoute(), {
        replaceUrl: true, state: {backNavigation: true}
      });
    }
  }
}
